import { isEmbeddedContent, isPresentationalPhrasingContent } from '../dom/content';
import { getElementContents, simplifyElement } from '../dom/element';
import { findAncestor, isElement } from '../dom/node';
import { paintNodesInRange } from '../dom/paint';

/**
 * Both semantic and obsolete tags that result in styling that we assume the user wants removed.
 */
export const STYLE_TAG_NAMES = [
  'B',
  'CODE',
  'EM',
  'I',
  'KBD',
  'MARK',
  'Q',
  'S',
  'SAMP',
  'SMALL',
  'STRONG',
  'TT',
  'U',
  'VAR',
  'BIG',
  'DEL',
  'FONT',
  'INS',
  'STRIKE',
];

export function removeStyleFromRange(container: Node, range: Range): Range {
  return paintNodesInRange(container, range, {
    shouldPaintNode: node => {
      return (
        !isEmbeddedContent(node) &&
        !!findAncestor(
          node,
          ancestor => isElement(ancestor) && (isElementWithStyle(ancestor) || isAgentStyledElement(ancestor)),
          container,
        )
      );
    },
    shouldSplitElement: element => isPresentationalPhrasingContent(element),
    paintElement: element => {
      if (isAgentStyledElement(element) && element.ownerDocument) {
        const isBlock = isAgentStyledBlockElement(element);
        const newElement = element.ownerDocument.createElement(isBlock ? 'div' : 'span');
        newElement.appendChild(getElementContents(element));
        return newElement;
      }

      const shouldSimplify = isStyleElement(element) || isPseudoStyleElement(element);

      element.removeAttribute('style');

      if (element.tagName === 'FONT') {
        element.removeAttribute('color');
        element.removeAttribute('face');
        element.removeAttribute('size');
      }

      if (shouldSimplify) {
        return simplifyElement(element);
      }

      return element;
    },
  });
}

function isElementWithStyle(element: Element) {
  return element.hasAttribute('style') || isStyleElement(element);
}

function isPseudoStyleElement(element: Element) {
  return (
    element.tagName === 'SPAN' && element.attributes.length === 1 && element.attributes[0].name === 'style'
  );
}

function isStyleElement(element: Element) {
  return STYLE_TAG_NAMES.includes(element.tagName);
}

const AGENT_STYLED_BLOCK_TAG_NAMES = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
const AGENT_STYLED_INLINE_TAG_NAMES = ['A'];

function isAgentStyledElement(element: Element) {
  return isAgentStyledBlockElement(element) || isAgentStyledInlineElement(element);
}

function isAgentStyledBlockElement(element: Element) {
  return AGENT_STYLED_BLOCK_TAG_NAMES.includes(element.tagName);
}

function isAgentStyledInlineElement(element: Element) {
  return AGENT_STYLED_INLINE_TAG_NAMES.includes(element.tagName);
}
